import * as React from 'react';
import { COMMERCE } from './config';
import Step3Delivery from './routes/checkout/step3-delivery';
import Step4Pay from './routes/checkout/step4-pay';
import { NoticeDialog } from './components/ui';
import { flowStateFromUrl, startOverUrl } from './utils/flow-state';
import { reportStepPageview } from './utils/pageview';
import { forgetPaidOrder } from './utils/complete-order';

// mtm-react-app-v3's own Easy Checkout flow container. One route, internal
// step machine via ?step= in the URL, in-memory state with sessionStorage
// persistence, a working back button, single mount.
//
// Steps 1-2 (date, plan) are a separate static page now. It hands over its
// answers in the URL (?dt=&est=&gift=&pln=…), so this app starts at Delivery.

const STEPS = ['delivery', 'pay'];

const REQUIRED_FOR_PAY = ['email', 'first', 'last', 'street', 'city', 'region', 'postal'];

const NOTICE_COPY = {
  delivery: { title: 'deliveryLostTitle', body: 'deliveryLostBody', cta: 'deliveryLostCta' },
  start: { title: 'sessionLostTitle', body: 'sessionLostBody', cta: 'sessionLostCta' },
};

const BACK_OF = { pay: 'delivery' };

// Any ?step= that isn't 'pay' (including the old start/building/plan values
// in links people already have) lands on Delivery.
function readStep() {
  try {
    return new URLSearchParams(window.location.search).get('step') === 'pay' ? 'pay' : 'delivery';
  } catch { return 'delivery'; }
}
function writeStep(step) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('step', step);
    window.history.pushState({ step }, '', url);
  } catch { /* no-op outside a browser */ }
}
function normaliseStep(step) {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('step') === step) return;
    url.searchParams.set('step', step);
    window.history.replaceState({ step }, '', url);
  } catch { /* no-op outside a browser */ }
}

const STORAGE_KEY = 'mtmEasyCheckoutV3';
function loadFlowState() {
  try { return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}
function saveFlowState(state) {
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* no-op */ }
}
function clearFlowState() {
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
}

// A promo/bonus a marketing page left in this origin's sessionStorage or
// cookie — only consulted when the URL doesn't carry one.
function storedValue(key) {
  try {
    return window.sessionStorage.getItem(key)
      || (document.cookie.match(new RegExp(`(^|; )${key}=([^;]*)`)) || [])[2] || '';
  } catch { return ''; }
}

// Saved answers first, then the query on top: the URL always wins (see
// utils/flow-state.js for the exact rules).
function readFlowState() {
  try {
    return flowStateFromUrl(window.location.search, loadFlowState(), storedValue);
  } catch { return flowStateFromUrl('', null); }
}

export default function App({ config: override, onComplete, onEvent } = {}) {
  const config = React.useMemo(() => ({
    ...defaultConfig,
    ...override,
    copy: { ...defaultConfig.copy, ...(override && override.copy) },
    commerce: { ...COMMERCE, ...(override && override.commerce) },
  }), [override]);

  const [step, setStep] = React.useState(readStep);
  const [notice, setNotice] = React.useState('');
  const [state, setState] = React.useState(readFlowState);

  // Google's badge may stay hidden only where its use is disclosed in text —
  // Step 4 is the one screen in this flow that actually says "This site is
  // protected by reCAPTCHA", so the class is scoped to this mount, not
  // applied globally. Ported from TTM's ttm-react-app subscribe-v2.page.jsx.
  React.useEffect(() => {
    document.body.classList.add('hide-recaptcha-badge');
    return () => document.body.classList.remove('hide-recaptcha-badge');
  }, []);

  // The browser tab / history / analytics title names the step, as the design's
  // build hints ask: "Checkout - Step 3 - Miss To Mrs Box" and ... Step 4 ....
  // (The page otherwise keeps the WordPress page's own title, which read the
  // same on every step.)
  React.useEffect(() => {
    document.title = `Checkout - Step ${step === 'pay' ? 4 : 3} - Miss To Mrs Box`;
  }, [step]);

  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const p = url.searchParams;
      // Only dt/est/gift/pln are ours; every other param (utm_*, promo, bonus,
      // looking_for, …) is left exactly as it came. The old wd/pln2 are dropped.
      if (state.wedDate) {
        p.set('dt', state.wedDate);
        if (state.wedDateEstimated) p.set('est', '1'); else p.delete('est');
      } else { p.delete('dt'); p.delete('est'); }
      p.delete('wd');
      if (state.audience === 'gift') p.set('gift', 'yes'); else p.delete('gift');
      if (state.planCode) p.set('pln', state.planCode); else p.delete('pln');
      p.delete('pln2');
      window.history.replaceState(window.history.state, '', url);
    } catch { /* no-op outside a browser */ }
  }, [state.wedDate, state.wedDateEstimated, state.audience, state.planCode]);

  React.useEffect(() => { saveFlowState(state); }, [state]);

  React.useEffect(() => {
    const onPop = () => setStep(readStep());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Only dt/est/gift/pln survive a refresh, not the delivery-details fields,
  // so a reload landing on ?step=pay with a valid plan but an empty address
  // must bounce back rather than let a purchase go out with no name/email/
  // shipping address on it. No date or plan at all ('start') means the
  // customer skipped the static Steps 1-2: there is nowhere in this app to
  // send them, so the popup's button takes them to the start page instead.
  const missingFor = (s) => {
    if (!state.wedDate || !state.planCode) return 'start';
    if (s === 'pay' && REQUIRED_FOR_PAY.some((k) => !state[k])) return 'delivery';
    return '';
  };
  const answerSig = [state.wedDate, state.planCode, ...REQUIRED_FOR_PAY.map((k) => state[k])].join(' ');
  React.useEffect(() => {
    const target = missingFor(step);
    if (!target) return;
    setNotice(target);
    emit('checkout-guard-redirect', {
      from: step,
      to: target,
      reason: !state.wedDate ? 'no-wedding-date' : !state.planCode ? 'no-plan-for-date' : 'missing-delivery-fields',
    });
    if (target !== 'start') {
      writeStep(target);
      setStep(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, answerSig]);

  const goTo = React.useCallback((next) => {
    writeStep(next);
    setStep(next);
    try { window.scrollTo(0, 0); } catch { /* no-op */ }
  }, []);
  const patch = React.useCallback((partial) => setState((s) => ({ ...s, ...partial })), []);

  const idx = STEPS.indexOf(step);
  const onNext = (extra) => (idx < STEPS.length - 1
    ? goTo(STEPS[idx + 1])
    : onComplete && Promise.resolve(onComplete({ ...state, ...(extra || {}) })).then((result) => {
      if (result && result.ok) clearFlowState();
      return result;
    }));
  const onBack = () => { if (BACK_OF[step]) goTo(BACK_OF[step]); };

  const emit = React.useCallback((name, extra) => {
    if (onEvent) onEvent(name, { ...state, ...(extra || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEvent, state]);

  React.useEffect(() => { normaliseStep(step); }, [step]);

  // One PostHog pageview per step change: forward, Back, the browser's Back /
  // Forward, and the bounce from a lost Pay step. Not the first screen (PostHog
  // counts the landing itself). After the title effect above, so the event
  // carries this step's title.
  const shownStep = React.useRef(null);
  React.useEffect(() => {
    const previous = shownStep.current;
    shownStep.current = step;
    if (previous !== null && previous !== step) reportStepPageview();
  }, [step]);

  React.useEffect(() => {
    emit('step-viewed', { step });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Delivery is where a new order starts, so a paid order left in this tab is done with
  // (see returnToPaidOrder in utils/complete-order.js).
  React.useEffect(() => {
    if (step === 'delivery') forgetPaidOrder();
  }, [step]);

  const startedCheckout = React.useRef(false);
  React.useEffect(() => {
    if (step !== 'delivery' || startedCheckout.current || missingFor(step) === 'start') return;
    startedCheckout.current = true;
    emit('checkout-start');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const shared = {
    config, state, patch, onEvent: emit, onNext, onBack,
  };

  // With no date/plan there is nothing to show behind the "start over" popup.
  let screen = null;
  if (missingFor(step) !== 'start') {
    screen = step === 'pay' ? <Step4Pay {...shared} /> : <Step3Delivery {...shared} />;
  }

  const noticeCopy = NOTICE_COPY[notice] || NOTICE_COPY.start;

  return (
    <>
      {screen}
      <NoticeDialog
        open={!!notice}
        title={config.copy[noticeCopy.title]}
        message={config.copy[noticeCopy.body]}
        actionLabel={config.copy[noticeCopy.cta]}
        onAction={() => {
          if (notice === 'start') window.location.assign(startOverUrl(window.location.search));
          else setNotice('');
        }}
      />
    </>
  );
}

const defaultConfig = {
  commerce: COMMERCE,
  copy: {
    sessionLostTitle: 'Let’s pick up from the start',
    sessionLostBody: 'We don’t have your answers any more, so we can’t show the right plan. Refreshing or reopening the page clears them. It only takes a moment to enter them again.',
    sessionLostCta: 'Start over',
    deliveryLostTitle: 'We need your delivery details again',
    deliveryLostBody: 'Refreshing the page clears the address and contact details, and we can’t take payment without them. Your plan and wedding date are still here — it’s just this one step.',
    deliveryLostCta: 'Re-enter details',
  },
};
